import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronRight, ArrowLeft, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  civilite: '', prenom: '', nom: '', email: '', code_interne: '',
  date_naissance: '', nom_naissance: '', nationalite: '', ville_naissance: '',
  departement_naissance: '', code_postal_naissance: '', numero_securite_sociale: '',
  adresse: '', code_postal: '', ville: '', pays: 'France FR',
  telephone: '', telephone_secondaire: '', langue: 'Français',
  statut: 'Employé', categorie_professionnelle: '', entreprise: '', fonction: '',
  manager: '', email_manager: '', date_entree_entreprise: '',
  identifiant_france_travail: '', niveau_etudes: '', activite_liberale: false,
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-lg font-bold text-slate-900 mt-8 mb-5 first:mt-0">{children}</h2>
);

const inputCls = "w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none transition-colors";
const selectCls = "w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none appearance-none";

const ApprenantDetailPage = () => {
  const { apprenantId } = useParams();
  const navigate = useNavigate();
  const isNew = !apprenantId || apprenantId === 'nouveau';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [entreprises, setEntreprises] = useState([]);

  const fetchApprenant = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await fetch(`${API}/donnees-apprenants/${apprenantId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, ...data }));
      } else {
        toast.error("Apprenant non trouvé");
        navigate('/donnees/apprenants');
      }
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [apprenantId, isNew, navigate]);

  useEffect(() => { fetchApprenant(); }, [fetchApprenant]);

  useEffect(() => {
    fetch(`${API}/entreprises`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(d => setEntreprises(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.nom?.trim()) { toast.error("Le nom de famille est obligatoire"); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/donnees-apprenants` : `${API}/donnees-apprenants/${apprenantId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(isNew ? 'Apprenant créé' : 'Apprenant mis à jour');
        if (isNew && data.apprenant_id) navigate(`/donnees/apprenants/${data.apprenant_id}`, { replace: true });
      } else { toast.error("Erreur lors de l'enregistrement"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer cet apprenant ?")) return;
    await fetch(`${API}/donnees-apprenants/${apprenantId}`, { method: 'DELETE', credentials: 'include' });
    toast.success("Apprenant supprimé");
    navigate('/donnees/apprenants');
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="h-8 w-48 bg-slate-100 rounded animate-pulse" />
          <div className="h-12 w-96 bg-slate-100 rounded animate-pulse" />
          <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="apprenant-detail-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Données</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="cursor-pointer hover:text-violet-500 transition-colors" onClick={() => navigate('/donnees/apprenants')}>Apprenants</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">{isNew ? 'Nouvel apprenant' : `${form.prenom} ${form.nom}`}</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/donnees/apprenants')} className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </button>
            <h1 className="text-2xl font-black text-slate-900" data-testid="page-title">
              {isNew ? 'Nouvel apprenant' : `${form.prenom} ${form.nom}`}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {!isNew && (
              <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="delete-btn">
                <Trash2 className="h-4 w-4 mr-1.5" /> Supprimer
              </Button>
            )}
          </div>
        </div>

        {/* Save Button */}
        <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-btn">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>

        {/* Form */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <SectionTitle>Informations générales</SectionTitle>

          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <div className="col-span-2 grid grid-cols-4 gap-x-8">
              <Field label="Civilité">
                <select className={selectCls} value={form.civilite} onChange={e => set('civilite', e.target.value)} data-testid="input-civilite">
                  <option value="">—</option>
                  <option value="M">M</option>
                  <option value="Mme">Mme</option>
                </select>
              </Field>
              <Field label="Prénom" className="col-span-3">
                <input className={inputCls} value={form.prenom} onChange={e => set('prenom', e.target.value)} placeholder="" data-testid="input-prenom" />
              </Field>
            </div>
            <Field label="Nom de famille *">
              <input className={inputCls} value={form.nom} onChange={e => set('nom', e.target.value)} placeholder="" data-testid="input-nom" />
            </Field>
            <Field label="Email">
              <input className={inputCls} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="" data-testid="input-email" />
            </Field>
            <Field label="Code interne">
              <input className={inputCls} value={form.code_interne} onChange={e => set('code_interne', e.target.value)} placeholder="" data-testid="input-code-interne" />
            </Field>
            <Field label="Date de naissance">
              <input className={inputCls} type="date" value={form.date_naissance} onChange={e => set('date_naissance', e.target.value)} data-testid="input-date-naissance" />
            </Field>
            <Field label="Nom de naissance">
              <input className={inputCls} value={form.nom_naissance} onChange={e => set('nom_naissance', e.target.value)} placeholder="" data-testid="input-nom-naissance" />
            </Field>
            <Field label="Nationalité">
              <input className={inputCls} value={form.nationalite} onChange={e => set('nationalite', e.target.value)} placeholder="" data-testid="input-nationalite" />
            </Field>
            <Field label="Ville de naissance">
              <input className={inputCls} value={form.ville_naissance} onChange={e => set('ville_naissance', e.target.value)} placeholder="" data-testid="input-ville-naissance" />
            </Field>
            <Field label="Département de naissance">
              <input className={inputCls} value={form.departement_naissance} onChange={e => set('departement_naissance', e.target.value)} placeholder="" data-testid="input-dept-naissance" />
            </Field>
            <Field label="Code postal du lieu de naissance">
              <input className={inputCls} value={form.code_postal_naissance} onChange={e => set('code_postal_naissance', e.target.value)} placeholder="" data-testid="input-cp-naissance" />
            </Field>
            <Field label="N° sécurité sociale">
              <input className={inputCls} value={form.numero_securite_sociale} onChange={e => set('numero_securite_sociale', e.target.value)} placeholder="" data-testid="input-secu" />
            </Field>
            <Field label="Adresse">
              <input className={inputCls} value={form.adresse} onChange={e => set('adresse', e.target.value)} placeholder="" data-testid="input-adresse" />
            </Field>
            <Field label="Code postal">
              <input className={inputCls} value={form.code_postal} onChange={e => set('code_postal', e.target.value)} placeholder="" data-testid="input-code-postal" />
            </Field>
            <Field label="Ville">
              <input className={inputCls} value={form.ville} onChange={e => set('ville', e.target.value)} placeholder="" data-testid="input-ville" />
            </Field>
            <Field label="Pays">
              <select className={selectCls} value={form.pays} onChange={e => set('pays', e.target.value)} data-testid="input-pays">
                <option value="France FR">France FR</option>
                <option value="CM">Cameroun CM</option>
                <option value="BE">Belgique BE</option>
                <option value="CH">Suisse CH</option>
                <option value="DE">Allemagne DE</option>
                <option value="ES">Espagne ES</option>
              </select>
            </Field>
            <Field label="Téléphone">
              <input className={inputCls} value={form.telephone} onChange={e => set('telephone', e.target.value)} placeholder="" data-testid="input-telephone" />
            </Field>
            <Field label="Téléphone secondaire">
              <input className={inputCls} value={form.telephone_secondaire} onChange={e => set('telephone_secondaire', e.target.value)} placeholder="" data-testid="input-telephone2" />
            </Field>
            <Field label="Langue dans l'Espace Apprenant">
              <select className={selectCls} value={form.langue} onChange={e => set('langue', e.target.value)} data-testid="input-langue">
                <option value="Français">Français</option>
                <option value="English">English</option>
                <option value="Español">Español</option>
              </select>
            </Field>
          </div>
        </div>

        {/* Statut Section */}
        <div className="bg-violet-50/50 border border-violet-100 rounded-2xl p-8">
          <SectionTitle>Statut</SectionTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Statut">
              <select className={selectCls} value={form.statut} onChange={e => set('statut', e.target.value)} data-testid="input-statut">
                <option value="Employé">Employé</option>
                <option value="Indépendant">Indépendant</option>
                <option value="Particulier">Particulier</option>
              </select>
            </Field>
            <Field label="Catégorie professionnelle">
              <select className={selectCls} value={form.categorie_professionnelle} onChange={e => set('categorie_professionnelle', e.target.value)} data-testid="input-categorie">
                <option value="">—</option>
                <option value="Cadre">Cadre</option>
                <option value="Employé">Employé</option>
                <option value="Technicien">Technicien</option>
                <option value="Agent de maîtrise">Agent de maîtrise</option>
                <option value="Ouvrier">Ouvrier</option>
              </select>
            </Field>
            <Field label="Entreprise">
              <select className={selectCls} value={form.entreprise} onChange={e => set('entreprise', e.target.value)} data-testid="input-entreprise">
                <option value="">—</option>
                {entreprises.map(ent => (
                  <option key={ent.entreprise_id} value={ent.name}>{ent.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Fonction">
              <input className={inputCls} value={form.fonction} onChange={e => set('fonction', e.target.value)} placeholder="" data-testid="input-fonction" />
            </Field>
          </div>
          <button onClick={() => navigate('/donnees/entreprises/nouveau')} className="text-sm text-violet-600 font-medium mt-3 hover:underline" data-testid="create-entreprise-link">
            Créer une entreprise
          </button>
        </div>

        {/* Champs personnalisés */}
        <div className="bg-slate-50/80 border border-slate-200 rounded-2xl p-8">
          <SectionTitle>Champs personnalisés</SectionTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Manager">
              <input className={inputCls} value={form.manager} onChange={e => set('manager', e.target.value)} placeholder="" data-testid="input-manager" />
            </Field>
            <Field label="E-mail du manager">
              <input className={inputCls} type="email" value={form.email_manager} onChange={e => set('email_manager', e.target.value)} placeholder="" data-testid="input-email-manager" />
            </Field>
            <Field label="Date d'entrée en entreprise">
              <input className={inputCls} type="date" value={form.date_entree_entreprise} onChange={e => set('date_entree_entreprise', e.target.value)} data-testid="input-date-entree" />
            </Field>
            <Field label="Identifiant France Travail">
              <input className={inputCls} value={form.identifiant_france_travail} onChange={e => set('identifiant_france_travail', e.target.value)} placeholder="" data-testid="input-france-travail" />
            </Field>
            <Field label="Niveau d'études">
              <select className={selectCls} value={form.niveau_etudes} onChange={e => set('niveau_etudes', e.target.value)} data-testid="input-niveau-etudes">
                <option value="">—</option>
                <option value="Sans diplôme">Sans diplôme</option>
                <option value="CAP/BEP">CAP/BEP</option>
                <option value="Bac">Bac</option>
                <option value="Bac+2">Bac+2</option>
                <option value="Bac+3">Bac+3</option>
                <option value="Bac+5">Bac+5</option>
                <option value="Bac+8">Bac+8</option>
              </select>
            </Field>
            <Field label="Activité libérale">
              <div className="flex items-center gap-3 h-10">
                <button onClick={() => set('activite_liberale', !form.activite_liberale)} className={`w-5 h-5 rounded border flex items-center justify-center ${form.activite_liberale ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`} data-testid="input-activite-liberale">
                  {form.activite_liberale && <span className="text-white text-xs font-bold">✓</span>}
                </button>
                <span className="text-sm text-slate-600">Activité libérale</span>
              </div>
            </Field>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ApprenantDetailPage;
